/**
 * A faint watermark across the page: who is looking, and when.
 *
 * It does not stop anyone photographing the screen. It makes a photograph
 * (or a screenshot) name the person who took it and the time, which is
 * what deters leaks and lets one be traced. The text is repeated on a
 * slant over the whole page and lets every click through.
 *
 * Returns the function that takes it off.
 */
const ID = "afri-watermark";

const tile = (text: string, dark: boolean): string => {
  const safe = text.replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const ink = dark ? "255,255,255" : "0,0,0";
  // Wide enough for the whole line on its slant, so no repeat cuts it off.
  const width = Math.max(420, Math.round(text.length * 8.2) + 80);
  const height = Math.round(width * 0.42) + 60;
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'>` +
    `<text x='24' y='${Math.round(height * 0.72)}' transform='rotate(-22 24 ${Math.round(height * 0.72)})' font-family='system-ui, sans-serif' ` +
    `font-size='14' fill='rgba(${ink},0.13)'>${safe}</text></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

export function showWatermark(who: string): () => void {
  if (typeof document === "undefined" || !who) return () => {};
  document.getElementById(ID)?.remove();
  const stamp = new Date().toISOString().slice(0, 16).replace("T", " ");
  const dark = window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  const layer = document.createElement("div");
  layer.id = ID;
  layer.setAttribute("aria-hidden", "true");
  const text = `${who}  ${stamp} UTC`;
  layer.dataset.text = text;
  Object.assign(layer.style, {
    position: "fixed",
    inset: "0",
    zIndex: "2147483000",
    pointerEvents: "none",
    backgroundImage: tile(text, dark),
    backgroundRepeat: "repeat",
  });
  document.body.appendChild(layer);
  // Put back if something takes it off the page while it is wanted.
  const guard = new MutationObserver(() => {
    if (!document.getElementById(ID)) document.body.appendChild(layer);
  });
  guard.observe(document.body, { childList: true });
  return () => {
    guard.disconnect();
    layer.remove();
  };
}
